import { Component, OnDestroy, OnInit } from '@angular/core';

import { Sort } from '@angular/material/sort';
import { Title } from '@angular/platform-browser';

import { ActivatedRoute, Router } from '@angular/router';

import { TranslocoService } from '@jsverse/transloco';
import { Subscription } from 'rxjs';
import { TabObject } from '@inmoment/the-kitchen';

import { HasTitle } from 'app/has-title';

import { AuthService } from 'app/providers/bridge-api/auth.service';
import { Dashboard, DashboardService } from 'app/providers/bridge-api/dashboard.service';
import { GroupService } from 'app/providers/semantria-api/group.service';
import {
  ActionPlan,
  ActionPlanService,
  Status,
} from 'app/providers/storage-api/actionplan.service';
import { AlertService } from 'app/services/alert.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { PreviousRouteService } from 'app/services/previous-route.service';
import { ActionPlanUtils } from 'app/utils/action-plan.utils';

import { ActionItemListFilter } from '../../components/action-item-list-filter/action-item-list-filter.component';

export interface ActionPlanDetailsTabOptions {
  sort?: Sort;
  searchValue?: string;
  filter?: ActionItemListFilter;
  expandedRowIds?: string[];
}

@EnableClassTelemetry()
@Component({
  selector: 'app-action-plan-details',
  templateUrl: './action-plan-details.component.html',
  styleUrls: ['./action-plan-details.component.scss'],
  standalone: false,
})
export class ActionPlanDetailsComponent extends HasTitle implements OnInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  private groupId: string;
  private shortenGroupId: string;

  private actionPlanId: string;
  actionPlan: ActionPlan;

  isLoading = false;
  isReadOnly;
  isSettingsDrawerOpen = false;

  title: string;
  backButtonText: string;
  backButtonUrl: string;
  dashboard: Dashboard;
  projectUrl: string;
  dashboardUrl: string;
  isExpired: boolean;
  dueDateStatusText: string;
  backButtonActionPlanString = '';
  backButtonDashboardString = '';
  isDashboardDeleted = false;

  readonly progressTabId = 'progress';
  readonly itemsTabId = 'items';
  readonly tasksTabId = 'tasks';
  tabs: TabObject[];
  activeTabId = this.progressTabId;
  itemsTabOptions: ActionPlanDetailsTabOptions;
  tasksTabOptions: ActionPlanDetailsTabOptions;
  translationSubscription: Subscription;

  constructor(
    title: Title,
    authService: AuthService,
    groupService: GroupService,
    private route: ActivatedRoute,
    private router: Router,
    private actionPlanService: ActionPlanService,
    private alertService: AlertService,
    private previousRouteService: PreviousRouteService,
    private dashboardService: DashboardService,
    private translocoService: TranslocoService
  ) {
    super(title);

    this.isReadOnly = authService.isReadOnly;

    this.groupId = groupService.getSelectedGroupId();
    this.shortenGroupId = groupService.getSelectedGroupId(true);

    this.actionPlanId = this.route.snapshot.paramMap.get('actionPlanId');
  }

  async ngOnInit(): Promise<void> {
    this.translationSubscription = this.translocoService
      .selectTranslateObject('action-plan-details-component')
      .subscribe((translations) => {
        this.setTitle(translations['action-plan']);
        this.backButtonActionPlanString = translations['back-button-action-plan'];
        this.backButtonDashboardString = translations['back-button-dashboard'];
      });

    this.setBackUrl();

    await this.load();

    this.isSettingsDrawerOpen = this.route.snapshot.queryParamMap.get('edit') === 'true';
  }

  ngOnDestroy(): void {
    this.translationSubscription?.unsubscribe();
  }

  private setBackUrl(): void {
    const previousUrl = this.previousRouteService.getPreviousUrl();

    if (previousUrl.match('^/groups/.*/projects/.*/dashboards/.*')) {
      this.backButtonText = this.backButtonDashboardString;
      this.backButtonUrl = previousUrl;
    } else {
      this.backButtonText = this.backButtonActionPlanString;
      this.backButtonUrl = `/groups/${this.shortenGroupId}/action-plans`;
    }
  }

  async load(): Promise<void> {
    if (!this.actionPlanId) {
      this.navigateToNotFound();
      return;
    }

    this.isLoading = true;

    try {
      this.actionPlan = await this.actionPlanService.getActionPlan(
        this.groupId,
        this.actionPlanId,
        { bundle: true } // for child hierarchy
      );
    } catch (error) {
      this.navigateToNotFound();
      return;
    }

    this.title = `${this.actionPlan.metadata.breakdown} - ${
      this.actionPlan.metric_name
    } ${this.translocoService.translate('action-plan-details-component.action-plan')}`;

    this.setDueDateInfo();

    this.tabs = this.getTabs();

    this.projectUrl = `/groups/${this.shortenGroupId}/projects/${this.actionPlan.metadata.projectId}`;
    this.dashboardUrl = `${this.projectUrl}/dashboards/${this.actionPlan.metadata.dashboardId}`;

    try {
      this.dashboard = await this.dashboardService.getDashboardById(
        this.actionPlan.metadata.dashboardId,
        this.actionPlan.metadata.projectId
      );
    } catch (error) {
      this.isDashboardDeleted = true;
    }

    this.isLoading = false;
  }

  private navigateToNotFound(): void {
    this.router.navigateByUrl('not-found', { skipLocationChange: true });
  }

  private setDueDateInfo(): void {
    this.isExpired = ActionPlanUtils.isExpired(this.actionPlan);
    this.dueDateStatusText = ActionPlanUtils.getExpiredLabel(this.actionPlan);
  }

  private getTabs(): TabObject[] {
    const itemsCount = this.actionPlan.total_child_count;

    let tasksCount = 0;
    this.actionPlan.action_items?.forEach((i) => (tasksCount += i.total_child_count));

    return [
      {
        id: this.progressTabId,
        title: this.translocoService.translate('action-plan-details-component.status-progress'),
      },
      {
        id: this.itemsTabId,
        title: this.translocoService.translate('action-plan-details-component.action-items', {
          count: itemsCount,
        }),
      },
      {
        id: this.tasksTabId,
        title: this.translocoService.translate('action-plan-details-component.tasks', {
          count: tasksCount,
        }),
      },
    ];
  }

  async onStatusChanged(status: Status): Promise<void> {
    const originalStatus = this.actionPlan.status;

    this.actionPlan.status = status;

    try {
      await this.actionPlanService.updateActionPlan(this.groupId, this.actionPlan);

      this.setDueDateInfo();

      this.alertService.showToast(
        this.translocoService.translate('action-plan-details-component.successful-update')
      );
    } catch (error) {
      this.actionPlan.status = originalStatus;

      this.alertService.showErrorToast(
        this.translocoService.translate('action-plan-details-component.failed-update'),
        error
      );
    }
  }

  onOpenSettingsDrawer(): void {
    this.isSettingsDrawerOpen = true;
  }

  onCloseSettingsDrawer(actionPlan?: ActionPlan): void {
    if (actionPlan) {
      this.actionPlan = actionPlan;

      this.setDueDateInfo();
    }

    this.isSettingsDrawerOpen = false;
  }

  onChildCountChange(): void {
    this.tabs = [];

    setTimeout(() => {
      this.tabs = this.getTabs();
    });
  }

  onNavigateToItem(itemId: string): void {
    this.itemsTabOptions = { expandedRowIds: [itemId] };

    this.activeTabId = this.itemsTabId;
  }
}
